import { ResponseResolver, restContext, RestRequest } from 'msw';
import {
  delayedResponse,
  delayedResponseWithDelay,
} from '@/utils/useMswCustomResponse';
import { CorporateApplicantResponse } from '../types/applicant';
import {
  CORPORATE_VEHICLE_OWNERSHIP_STATUS,
  listingStatusOptions,
} from '../../constants/corporateApplication';
import { CORPORATE_USAGE_PURPOSE } from '../../constants/monthlyApplications';

const delayedPreApplicationResponse = delayedResponseWithDelay(1000);

const putCorporaApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedResponse(ctx.status(204));
};

const putCorporateApplicant: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedResponse(ctx.status(204));
};

const postCorporatePreApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedPreApplicationResponse(
    ctx.status(201),
    ctx.json({ applicationId: 1 }),
  );
};

/**
 * 法人申込者情報のベースモックデータ
 * E2Eテストなどで再利用可能
 */
export const baseCorporateApplicantResponse: CorporateApplicantResponse = {
  monthlyApplicationId: 110,
  parkingId: 451265,
  parkingName: '決済テスト用駐車場（法人モック）',
  parkingAddress: '東京都渋谷区道玄坂1-2-3',
  corporateName: 'ナビパーク株式会社',
  corporateNameKana: 'ナビパークカブシキガイシャ',
  headquartersPostalCode: '1500043',
  headquartersAddressRegion: '東京都渋谷区',
  headquartersAddressDetail: '道玄坂1丁目2番3号',
  headquartersPhoneNumber: ['03', '1234', '5678'],
  representativeLastName: '田中',
  representativeFirstName: '太郎',
  representativeLastNameKana: 'タナカ',
  representativeFirstNameKana: 'タロウ',
  industry: 'CONSTRUCTION',
  industryMapping: [
    { enum: 'CONSTRUCTION', label: '建設業' },
    { enum: 'MANUFACTURING', label: '製造業' },
    { enum: 'INFORMATION_TECHNOLOGY', label: '情報通信業' },
    { enum: 'TRANSPORTATION_COMMUNICATION', label: '運輸・通信業' },
    { enum: 'WHOLESALE_RETAIL_FOOD', label: '卸売・小売・飲食業' },
    { enum: 'FINANCE_INSURANCE', label: '金融・保険業' },
    { enum: 'REAL_ESTATE', label: '不動産業' },
    { enum: 'SERVICE', label: 'サービス業' },
    { enum: 'EDUCATION', label: '教育' },
    { enum: 'HEALTHCARE', label: '医療・福祉' },
    { enum: 'OTHER', label: 'その他' },
  ],
  listingStatus: 'STANDARD',
  listingStatusMapping: listingStatusOptions.map((x) => ({
    enum: x.id,
    label: x.label,
  })),
  departmentName: 'システム開発部',
  contactPersonLastName: '佐藤',
  contactPersonFirstName: '花子',
  contactPersonLastNameKana: 'サトウ',
  contactPersonFirstNameKana: 'ハナコ',
  notificationEmail: 'contact@navipark.example.com',
  departmentAddressPostalCode: '1500043',
  departmentAddressRegion: '東京都渋谷区',
  departmentAddressDetail: '道玄坂1丁目2番3号 ナビパークビル5F',
  departmentPhoneNumber: ['03', '9876', '5432'],
  vehicleOwnershipStatus:
    CORPORATE_VEHICLE_OWNERSHIP_STATUS.UNSPECIFIED_VEHICLE,
  vehicleOwnershipStatusMapping: [
    { enum: 'CONTRACT_VEHICLE_DECIDED', label: '契約車両が決定している' },
    { enum: 'CONTRACT_VEHICLE_UNDETERMINED', label: '契約車両未決定' },
    { enum: 'UNSPECIFIED_VEHICLE', label: '車両を特定せず使用する' },
  ],
  usagePurpose: CORPORATE_USAGE_PURPOSE.COMPANY_OWNED,
  usagePurposeMapping: [
    {
      enum: CORPORATE_USAGE_PURPOSE.COMPANY_OWNED,
      label: '法人利用（社有車）',
    },
    {
      enum: CORPORATE_USAGE_PURPOSE.EMPLOYEE_OWNED,
      label: '法人利用（従業員所有車）',
    },
  ],
  parkedVehicleType: 'セダン',
  parkedVehicleColor: '白',
  scheduledDeliveryDate: '2025-01-15',
  noVehicleReason: null,
  frontVehicleFileUrl: 'https://picsum.photos/700/500?random=1',
  backVehicleFileUrl: 'https://picsum.photos/700/500?random=2',
  sideVehicleFileUrl: 'https://picsum.photos/700/500?random=3',
  carInspectionFileUrl: 'https://picsum.photos/700/500?random=4',
  companyRegistryFiles: [
    {
      id: 9981,
      url: 'https://pdfobject.com/pdf/sample.pdf?test-data=/api/file/',
    },
    {
      id: 9982,
      url: 'https://pdfobject.com/pdf/sample.pdf?test-data=/api/file/',
    },
  ],
  parkedVehicleCategory: 'BIKE',
  bikeDisplacement: 'UNDER_125CC',
  vehicleNumber: {
    localName: '品川',
    classificationNumber: '123',
    hiragana: 'あ',
    serialNumber: '12-34',
  },
  additionalVehicleNumbers: '品川330 あ 12-34',
};

/**
 * 法人申込者情報取得ハンドラーを生成する（オーバーライド可能）
 * E2Eテストなどで特定のフィールドを上書きしたい場合に使用
 */
export const createGetCorporateApplicant = (
  override?: Partial<CorporateApplicantResponse>,
): ResponseResolver<RestRequest, typeof restContext> => {
  return async (_req, _res, ctx) => {
    return delayedResponse(
      ctx.status(200),
      ctx.json<CorporateApplicantResponse>({
        ...baseCorporateApplicantResponse,
        ...override,
      }),
    );
  };
};

/**
 * 法人申込者情報を取得する
 */
const getCorporateApplicant = createGetCorporateApplicant();

const mockCorporateMonthly = {
  putCorporaApplication,
  putCorporateApplicant,
  postCorporatePreApplication,
  getCorporateApplicant,
};

export default mockCorporateMonthly;
